import React, { useEffect, useState } from "react";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";

import ContextProviders from "context/ContextProviders";
import { ViewEnum, ViewType } from "custom-types/Finder";
import { FinderPageContext } from "custom-types/FinderPage";
import { useEventTracker } from "hooks/useEventTracker";
import useSplit from "hooks/useSplit";
import { COLLAPSE_HEADER, EXPAND_HEADER } from "redux/reducers/header";
import getFinderContextData from "utils/finder/getContextData";
import { Action, Category } from "utils/trackEvent";

import FinderHead from "components/Finder/FinderHead";
import FinderSprites from "components/Finder/FinderSprites/FinderSprites";
import ListingsWrapper from "components/Finder/ListingsWrapper";
import MapWrapper from "components/Finder/MapWrapper";

const FinderPage: NextPage<FinderPageContext> = ({
  locationContext,
  configContext,
  mapContext,
}) => {
  const dispatch = useDispatch();

  const {
    dispatch: { track },
  } = useSplit();

  const { retailType, isLocationPage } = configContext;

  const { query, pathname, replace } = useRouter();

  useEffect(() => {
    track("leafly_pageView_frontend");
    track("webFinder_pageView_frontend", { retailType });

    if (!isLocationPage && query?.location && query.location.length > 0) {
      replace({ pathname });
    }
  }, []);

  const [listingPosition, setListingPosition] = useState<ViewType>(
    ViewEnum.HALF
  );

  const handleSetListingPosition = (type: ViewType) => {
    if (type === ViewEnum.MAP || type === ViewEnum.LIST) {
      dispatch({ type: COLLAPSE_HEADER });
    } else {
      dispatch({ type: EXPAND_HEADER });
    }

    setListingPosition(type);
  };

  const { trackEvent } = useEventTracker();

  function trackMobileViewClickEvent(listingPosition: ViewEnum) {
    if (listingPosition === ViewEnum.LIST) {
      trackEvent(Category.finder, Action.click, `${ViewEnum.LIST} view`);
    } else if (listingPosition === ViewEnum.MAP) {
      trackEvent(Category.finder, Action.click, `${ViewEnum.MAP} view`);
    }
  }
  return (
    <>
      <ContextProviders
        configContext={configContext}
        mapContext={mapContext}
        locationContext={locationContext}
      >
        <FinderHead />
        <div className="finder-page flex flex-col bg-leafly-white z-10">
          <FinderSprites />
          <ListingsWrapper
            listingPosition={listingPosition}
            setListingPosition={handleSetListingPosition}
            trackMobileViewClickEvent={trackMobileViewClickEvent}
          />
          <MapWrapper
            listingPosition={listingPosition}
            setListingPosition={handleSetListingPosition}
            trackMobileViewClickEvent={trackMobileViewClickEvent}
          />
        </div>
      </ContextProviders>
      <style jsx global>{`
        .finder-page {
          --finder-desktop-listing-width: 380px;
          --finder-desktop-min-breakpoint: 1023px;
          --finder-collapsed-header-height: 48px;
          --finder-mobile-bottom-touchpoint: 104px;
          --finder-mobile-top-touchpoint: var(--finder-collapsed-header-height);
        }
        html {
          height: 100%;
        }
        ::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        ::-webkit-scrollbar-track {
          background: gainsboro;
          border-radius: 5px;
        }
        ::-webkit-scrollbar-thumb {
          background: var(--color-green);
          border-radius: 5px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: var(--color-default);
        }
        .active-filters {
          -webkit-overflow-scrolling: touch;
        }
        .active-filters::-webkit-scrollbar {
          display: none;
        }
        .scrollSnapWrapper {
          scroll-snap-points-x: repeat(272px);
          scroll-snap-type: x mandatory;
          scroll-padding: 16px;
          list-style: none;
        }
        .removeScrollbar {
          overflow-x: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .removeScrollbar::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
      `}</style>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<FinderPageContext> = async (
  ctx
) => {
  const contextData = await getFinderContextData(ctx);

  return {
    props: {
      ...contextData,
      // The following props are used only in _app.tsx
      disableHeaderCollapseOnScroll: true,
      hideFooter: true,
      hideHeader: false,
    },
  };
};

export default FinderPage;
